import Component from '../abstracts/Component';
import WishlistLogic from '../logics/WishlistLogic';
import TrackingComponent from '../components/TrackingComponent';
import { algoliaAddToWishlist } from "../utils/algoliaTrackings";
import vex from 'vex-js';

export default class WishlistComponent extends Component {
  constructor(el) {
    super(el);
    this.wishlistLogic = new WishlistLogic();
    this.trackingComponent = new TrackingComponent();

    this.wishlistEmptyMessage = document.querySelector(
      this.SELECTORS.wishlistEmptyMessage
    );
    this.wishlistWrapper = document.querySelector(
      this.SELECTORS.wishlistWrapper
    );

    // PDP wishlist button
    this.pdpWishlistButton = document.querySelector(
      this.SELECTORS.pdpWishlistButton
    );

    // all wishlist buttons
    this.wishBtns = document.querySelectorAll(this.SELECTORS.wishBtn);

    //wishlist page
    this.wishRemoveBtns = document.querySelectorAll(
      this.SELECTORS.wishRemoveBtn
    );
    this.sizeSelectors = document.querySelectorAll(this.SELECTORS.sizeSelector);

    // Wishlist items
    this.wishlistCardItems = document.querySelectorAll(
      this.SELECTORS.wishlistCardItem
    );
  }

  get COMPONENTNAME() {
    return 'WishlistComponent';
  }

  get SELECTORS() {
    return {
      // Wishlist wrapper elements
      wishlistEmptyMessage: '[data-wishlist-empty-message]',
      wishlistWrapper: '[data-wishlist-products-wrapper]',
      // Wishlist card main actions
      wishBtn: '[data-wishlist-btn]',
      pdpWishlistButton: '[data-pdp-wishlist-button]',
      pdpWishlistButtonAttribute: 'data-pdp-wishlist-button',
      wishRemoveBtn: '[data-wish-remove]',
      sizeSelector: '[data-size-selector]',
      // Wishlist card item and interactive elements
      wishlistCardItem: '[data-wishlist-card-item]',
      attributeProductCode: 'data-wishlist-item-product-code',
      attributeMainButton: 'data-wishlist-item-add-to-cart',
      attributeMainButtonAction: 'data-add-product-from-wishlist-action',

      attributeProductIsUniqueSize: 'data-wishlist-product-is-unique-size',
      attributeProductIsOutOfStock: 'data-wishlist-product-is-out-of-stock',

      attributeSizeSelectorButton: 'data-wishlist-product-size-button',
      attributeNotifyMeSizeFieldId:
        'data-wishlist-notify-me-product-size-field-id',
      attributeSizeSelectorButtonValue:
        'data-wishlist-product-size-button-value',
      attributeSizeSelectorButtonAction:
        'data-wishlist-product-size-button-action',
      attributeSizeSelectorButtonOutOfStock:
        'data-wishlist-product-size-button-out-of-stock',

      attributeCloseSizeSelectorLayer: 'data-wishlist-item-size-selector-close',
      attributeCloseNotifyMeLayer: 'data-wishlist-item-notify-me-layer-close',

      attributeSizeSelectorLayerId: 'data-wishlist-item-size-selector-layer-id',
      attributeNotifyMeLayerId: 'data-wishlist-item-notify-me-layer-id',

      attributeSizeSelectorLayerTargetId:
        'data-wishlist-item-size-selector-layer-target-id',
      attributeNotifyMeLayerTargetId:
        'data-wishlist-item-notify-me-layer-target-id',

      // Notify me elements
      attributeNotifyMeFormSubmitButton:
        'data-wishlist-item-notify-me-form-submit-button',
      attributeNotifyMeFormId: 'data-wishlist-item-notify-me-form-target',
      attributeNotifyMeFormSubmitButtonOkTarget:
        'data-wishlist-item-notify-me-text-ok-id',
      attributeNotifyMeFormSubmitButtonWaitTarget:
        'data-wishlist-item-notify-me-text-wait-id',
      attributeNotifyMeFormErrorTarget: 'data-wishlist-item-notify-me-error-id',
      attributeNotifyMeFieldWrapperTarget:
        'data-wishlist-item-notify-me-field-wrapper-id',
      attributeNotifyMeFieldTarget: 'data-wishlist-item-notify-me-field-id',
    };
  }

  get CLASSES() {
    return {
      buttonLoadingClass: '--loading',
      error: 'error',
      floatingLayoutOpen: '--open',
      hiddenElement: '--hidden',
      showElement: '--show',
      deleteCard: '--action-delete',
      disableCard: '--action-disabled',
      buttonBlocked: '--blocked',
    };
  }

  // Handle click event for all wishlist buttons

  // This method binds click events to all wishlist buttons found in the DOM (except the PDP wishlist button)
  handleWishlistClick(wishBtns) {
    if (!wishBtns?.length) return;
    wishBtns.forEach((button) => {
      button.addEventListener('click', (event) => this.handleSingleWishlistClick(event, button));
    });
  }

  // Handle click event for a single wishlist button
  async handleSingleWishlistClick(event, button) {
    event.preventDefault();
    event.stopPropagation();
    const {wishlistBtn, pdpWishlistButton, code, isPdp} = button.dataset;
    const isProductDetailPage = isPdp === 'true';
    let isInWishlist = null;
    if (isProductDetailPage) {
      isInWishlist = pdpWishlistButton === 'true';
    } else {
      isInWishlist = wishlistBtn === 'true';
    }
    try {
      if (isInWishlist) {
        await this.removeFromWishlist(button, code, isProductDetailPage);
      } else {
        await this.addToWishlist(button, code, isProductDetailPage);
      }
    } catch (error) {
      console.error('Error handling wishlist action:', error);
    }
  }

  // Add product to wishlist
  async addToWishlist(button, productCode, isPdp) {
    const {wishAddUrl} = button.dataset;
    const response = this.wishlistLogic.addToWishlist(wishAddUrl, productCode);
    if (!response) return;
    try {
      await response;
      this.$emit(this.$customEvents.PRODUCT_EVENTS.addedtowish, {
        productCode,
      });
      algoliaAddToWishlist(productCode);
      this.trackingComponent.trackAction('wishlistAdd', button.dataset, button);
      // Update UI based on page type
      this.updateWishlistButtonUI(button, true, isPdp);
      // Update button state based of static PDP button or dynamic button
      if (isPdp) button.dataset.pdpWishlistButton = 'true';
      else button.dataset.wishlistBtn = 'true';
    } catch (error) {
      console.error('Failed to add to wishlist:', error);
      throw error;
    }
  }

  // Remove product from wishlist
  async removeFromWishlist(button, productCode, isPdp) {
    const {wishRemoveUrl} = button.dataset;
    const response = this.wishlistLogic.removeFromWishlist(
      wishRemoveUrl,
      productCode
    );
    if (!response) return;
    try {
      await response;
      this.$emit(this.$customEvents.PRODUCT_EVENTS.removedfromwish);
      // Update UI based on page type
      this.updateWishlistButtonUI(button, false, isPdp);
      // Update button state based of static PDP button or dynamic button
      if (isPdp) button.dataset.pdpWishlistButton = 'false';
      else button.dataset.wishlistBtn = 'false';
    } catch (error) {
      console.error('Failed to remove from wishlist:', error);
      throw error;
    }
  }

  // Update wishlist button UI based on page type
  updateWishlistButtonUI(button, isAdded, isPdp) {
    if (isPdp) {
      this.wishlistLogic.updatePDPButton(button, isAdded, true);
    } else {
      this.wishlistLogic.updateWishlistIcon(button, !isAdded);
    }
  }

  // WISHLIST PAGE //
  handleWishlistRequest({
                          requestMethod = 'GET',
                          productCode,
                          action,
                          payload = {},
                          updateCart = false,
                          callback = null,
                        }) {
    const prodCard = document.querySelector(
      `[data-card-code="${productCode}"]`
    );
    if (prodCard) {
      prodCard.classList.add(this.CLASSES.disableCard);
    }

    fetch(action, {
      method: requestMethod,
      body: payload,
    })
      .then((fetchResponse) => {
        let responseSuccess = false;
        if (fetchResponse.ok) {
          responseSuccess = true;
        }
        fetchResponse.text().then((responseAsText) => {
          // Handle low stock modal (status 651)
          if (responseAsText == '651') {
            const targetElement = document.querySelector(`[data-wishlist-item-product-code="${productCode}"]`);
            if (targetElement) {
              const lowStockUrl = targetElement.getAttribute('data-lowstock-cart-qty');
              if (lowStockUrl) {
                this.openModal(lowStockUrl + productCode);
              }
            }
            return;
          }

          if (updateCart)
            this.$emit(this.$customEvents.MINICART_EVENTS.update, {
              quickNotify: true,
            });
          if (callback) callback(responseSuccess, responseAsText);
        });
        this.trackingComponent.actions.addToCart(productCode);
      })
      .catch((error) => {
        if (callback) callback(false, error);
      })
      .finally(() => {
        if (prodCard) {
          prodCard.classList.remove(this.CLASSES.disableCard);
        }
      });
  }

  // Handle notify me show events to do some
  handleActionNotifyMeLayout(layoutTarget, action = 'open') {
    let inputTarget = null;
    let formError = null;
    if (action === 'open') {
      // Show the notify me layout
      layoutTarget.classList.add(this.CLASSES.floatingLayoutOpen);
      // Remove form error text and hide it
      formError = document.getElementById(
        layoutTarget.getAttribute(
          this.SELECTORS.attributeNotifyMeFormErrorTarget
        )
      );
      if (formError) {
        formError.classList.add(this.CLASSES.hiddenElement);
        formError.innerHTML = '';
      }
      // Remove form email field error and current value
      document
        .getElementById(
          layoutTarget.getAttribute(
            this.SELECTORS.attributeNotifyMeFieldWrapperTarget
          )
        )
        ?.classList.remove(this.CLASSES.error);
      inputTarget = document.getElementById(
        layoutTarget.getAttribute(this.SELECTORS.attributeNotifyMeFieldTarget)
      );
      if (inputTarget) inputTarget.value = '';
      // Hide the submit button Ok text
      document
        .getElementById(
          layoutTarget.getAttribute(
            this.SELECTORS.attributeNotifyMeFormSubmitButtonOkTarget
          )
        )
        ?.classList.add(this.CLASSES.hiddenElement);
      // Show the submit button wait text
      document
        .getElementById(
          layoutTarget.getAttribute(
            this.SELECTORS.attributeNotifyMeFormSubmitButtonWaitTarget
          )
        )
        ?.classList.remove(this.CLASSES.hiddenElement);
    } else {
      // Hide the notify me layout
      layoutTarget.classList.remove(this.CLASSES.floatingLayoutOpen);
    }
  }

  wishlistCardItemEvents() {
    // Event handler for all interactive elements for each wishlist card element
    if (this.wishlistCardItems) {
      this.wishlistCardItems.forEach((currentWishlistCard) => {
        currentWishlistCard.addEventListener('click', (event) => {
          let eventTarget = event.target;
          // this awful s**t is necessary because browser translator could insert some HTML inside button content, "ruining" original event target...
          if (!(eventTarget.tagName === 'BUTTON')) {
            eventTarget = eventTarget.parentElement;
            while (!(eventTarget.tagName === 'BUTTON')) {
              eventTarget = eventTarget.parentElement;
            }
          }
          let layoutTarget = null;
          let productIsUniqueSize = false;
          let productIsOutOfStock = false;
          let productCode = null;

          let productSelectedSize = null;
          let productSelectedAction = null;
          let productSelectedSizeIsOutOfStock = false;

          let productNotifyMeFieldTarget = null;
          let productNotifyMeFormTarget = null;
          let productNotifyMeFormError = null;

          // Check if target is card primary action button
          if (
            eventTarget.getAttribute(this.SELECTORS.attributeMainButton) ===
            'true'
          ) {
            productIsUniqueSize =
              eventTarget.getAttribute(
                this.SELECTORS.attributeProductIsUniqueSize
              ) === 'true';
            productIsOutOfStock =
              eventTarget.getAttribute(
                this.SELECTORS.attributeProductIsOutOfStock
              ) === 'true';
            // Product is NOT unique size
            if (!productIsUniqueSize) {
              // Show size selector
              layoutTarget = document.querySelector(
                `[${this.SELECTORS.attributeSizeSelectorLayerId}="${eventTarget.getAttribute(this.SELECTORS.attributeSizeSelectorLayerTargetId)}"]`
              );
              if (layoutTarget) {
                layoutTarget.classList.add(this.CLASSES.floatingLayoutOpen);
              }
            }
            // Product is unique size
            else {
              // Product is out of stock, show notify me layer
              if (productIsOutOfStock) {
                layoutTarget = document.querySelector(
                  `[${this.SELECTORS.attributeNotifyMeLayerId}="${eventTarget.getAttribute(this.SELECTORS.attributeNotifyMeLayerTargetId)}"]`
                );
                if (layoutTarget)
                  this.handleActionNotifyMeLayout(layoutTarget, 'open');
              }
              // Product is NOT out of stock, call to add to cart
              else {
                productSelectedAction = eventTarget.getAttribute(
                  this.SELECTORS.attributeMainButtonAction
                );
                productCode = eventTarget.getAttribute(
                  this.SELECTORS.attributeProductCode
                );
                this.handleWishlistRequest({
                  requestMethod: 'POST',
                  productCode,
                  action: productSelectedAction,
                  updateCart: true,
                });
              }
            }
          }
          // Check if target is a size button
          else if (
            eventTarget.getAttribute(
              this.SELECTORS.attributeSizeSelectorButton
            ) === 'true'
          ) {
            productSelectedSize = eventTarget.getAttribute(
              this.SELECTORS.attributeSizeSelectorButtonValue
            );
            productSelectedAction = eventTarget.getAttribute(
              this.SELECTORS.attributeSizeSelectorButtonAction
            );
            productSelectedSizeIsOutOfStock =
              eventTarget.getAttribute(
                this.SELECTORS.attributeSizeSelectorButtonOutOfStock
              ) === 'true';
            // Current size button is Out of stock, set field with value for notify me form and show the layer
            if (productSelectedSizeIsOutOfStock) {
              productNotifyMeFieldTarget = document.getElementById(
                `${eventTarget.getAttribute(this.SELECTORS.attributeNotifyMeSizeFieldId)}`
              );
              if (productNotifyMeFieldTarget && productSelectedSize) {
                productNotifyMeFieldTarget.value = productSelectedSize;
              }
              layoutTarget = document.querySelector(
                `[${this.SELECTORS.attributeNotifyMeLayerId}="${eventTarget.getAttribute(this.SELECTORS.attributeNotifyMeLayerTargetId)}"]`
              );
              if (layoutTarget)
                this.handleActionNotifyMeLayout(layoutTarget, 'open');
            }
            // Current size button is NOT Out of stock, call to add to cart for specific size
            else {
              productCode = eventTarget.getAttribute(
                this.SELECTORS.attributeProductCode
              );
              this.handleWishlistRequest({
                requestMethod: 'POST',
                productCode,
                action: productSelectedAction,
                updateCart: true,
                callback: () => {
                  const closeElementsDOM = [
                    document.querySelector(
                      `[${this.SELECTORS.attributeSizeSelectorLayerId}="${eventTarget.getAttribute(this.SELECTORS.attributeSizeSelectorLayerTargetId)}"]`
                    ),
                  ];
                  closeElementsDOM.forEach((currentElementDOM) => {
                    currentElementDOM.classList.remove(
                      this.CLASSES.floatingLayoutOpen
                    );
                  });
                },
              });
            }
          }
          // Check if target is notify me form submit button
          else if (
            eventTarget.getAttribute(
              this.SELECTORS.attributeNotifyMeFormSubmitButton
            ) === 'true'
          ) {
            // Prevent default form behavior
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            // Get the product code from button data attribute
            productCode = eventTarget.getAttribute(
              this.SELECTORS.attributeProductCode
            );
            // Identify the notify me form data
            productNotifyMeFormTarget = document.getElementById(
              `${eventTarget.getAttribute(this.SELECTORS.attributeNotifyMeFormId)}`
            );
            if (productNotifyMeFormTarget) {
              // Hide the button wait text and set it the loading class
              document
                .getElementById(
                  eventTarget.getAttribute(
                    this.SELECTORS.attributeNotifyMeFormSubmitButtonWaitTarget
                  )
                )
                ?.classList.add(this.CLASSES.hiddenElement);
              eventTarget.classList.add(this.CLASSES.buttonLoadingClass);
              // Identify form error element and hide it
              productNotifyMeFormError = document.getElementById(
                eventTarget.getAttribute(
                  this.SELECTORS.attributeNotifyMeFormErrorTarget
                )
              );
              if (productNotifyMeFormError)
                productNotifyMeFormError?.classList.add(
                  this.CLASSES.hiddenElement
                );
              // Identify notify me email field and remove the error class
              document
                .getElementById(
                  eventTarget.getAttribute(
                    this.SELECTORS.attributeNotifyMeFieldWrapperTarget
                  )
                )
                ?.classList.remove(this.CLASSES.error);
              // Get the notify me form data and prepare the request
              this.handleWishlistRequest({
                requestMethod: productNotifyMeFormTarget.method,
                productCode,
                action: productNotifyMeFormTarget.action,
                payload: new FormData(productNotifyMeFormTarget),
                callback: (success, response) => {
                  // Remove the loading class to the submit button
                  eventTarget.classList.remove(this.CLASSES.buttonLoadingClass);
                  // Response is ok
                  if (success) {
                    // set the submit button text ok as visible
                    document
                      .getElementById(
                        eventTarget.getAttribute(
                          this.SELECTORS
                            .attributeNotifyMeFormSubmitButtonOkTarget
                        )
                      )
                      ?.classList.remove(this.CLASSES.hiddenElement);
                    setTimeout(() => {
                      layoutTarget = document.querySelector(
                        `[${this.SELECTORS.attributeNotifyMeLayerId}="${eventTarget.getAttribute(this.SELECTORS.attributeNotifyMeLayerTargetId)}"]`
                      );
                      if (layoutTarget)
                        this.handleActionNotifyMeLayout(layoutTarget, 'close');
                    }, 1500);
                  }
                  // Response is not OK
                  else {
                    // set submit button wait text visible
                    document
                      .getElementById(
                        eventTarget.getAttribute(
                          this.SELECTORS
                            .attributeNotifyMeFormSubmitButtonWaitTarget
                        )
                      )
                      ?.classList.remove(this.CLASSES.hiddenElement);
                    // Identify notify me email field and add the error class
                    document
                      .getElementById(
                        eventTarget.getAttribute(
                          this.SELECTORS.attributeNotifyMeFieldWrapperTarget
                        )
                      )
                      ?.classList.add(this.CLASSES.error);
                    // Set the error visible and the response as its inner text
                    if (productNotifyMeFormError) {
                      productNotifyMeFormError?.classList.remove(
                        this.CLASSES.hiddenElement
                      );
                      productNotifyMeFormError.innerHTML = response;
                    }
                  }
                },
              });
            }
          }
          // Check if target is close notify me layer
          else if (
            eventTarget.getAttribute(
              this.SELECTORS.attributeCloseNotifyMeLayer
            ) === 'true'
          ) {
            // Find the layer by ID attribute and delete the open class
            layoutTarget = document.querySelector(
              `[${this.SELECTORS.attributeNotifyMeLayerId}="${eventTarget.getAttribute(this.SELECTORS.attributeNotifyMeLayerTargetId)}"]`
            );
            if (layoutTarget) {
              this.handleActionNotifyMeLayout(layoutTarget, 'close');
            }
          }
          // Check if target is close size selector layer
          else if (
            eventTarget.getAttribute(
              this.SELECTORS.attributeCloseSizeSelectorLayer
            ) === 'true'
          ) {
            // Find the layer by ID attribute and delete the open class
            layoutTarget = document.querySelector(
              `[${this.SELECTORS.attributeSizeSelectorLayerId}="${eventTarget.getAttribute(this.SELECTORS.attributeSizeSelectorLayerTargetId)}"]`
            );
            if (layoutTarget) {
              layoutTarget.classList.remove(this.CLASSES.floatingLayoutOpen);
            }
          }
        });
      });
    }
  }

  handleDeleteFromWishlist() {
    if (this.wishRemoveBtns) {
      this.wishRemoveBtns.forEach((button) => {
        button.addEventListener('click', (event) => {
          const url = button.dataset.wishRemoveUrl;
          const response = this.wishlistLogic.removeFromWishlist(
            url,
            button.dataset.code
          );
          if (response) {
            const prodCard = document.querySelector(
              '[data-card-code="' + button.dataset.code + '"]'
            );
            if (prodCard) {
              prodCard.classList.add(this.CLASSES.deleteCard);
              setTimeout(() => {
                prodCard.remove();
                this.$emit(this.$customEvents.MINIWISHLIST_EVENTS.update);
                // If quantity of elements is 0, show empty message and hide main wrapper
                const remainingWishlistItems = document.querySelectorAll(
                  this.SELECTORS.wishRemoveBtn
                );
                if (remainingWishlistItems.length < 1) {
                  this.wishlistWrapper.classList.add(
                    this.CLASSES.hiddenElement
                  );
                  this.wishlistEmptyMessage.classList.remove(
                    this.CLASSES.hiddenElement
                  );
                  this.wishlistEmptyMessage.classList.add(
                    this.CLASSES.showElement
                  );
                }
              }, 1000);
            }
          }
        });
      });
    }
  }

  listenSelectWishSize() {
    if (this.sizeSelectors) {
      this.sizeSelectors.forEach((sizeSelector) => {
        sizeSelector.addEventListener('change', (event) => {
          if (event.target.selectedOptions.length > 0) {
            const selectedOption = event.target.selectedOptions[0];
            sizeSelector.parentElement.classList.remove(this.CLASSES.error);

            const wishlistInfo = sizeSelector.closest('[data-wishlist-info]');
            let notifymeButton = null;
            let addToCartButton = null;
            if (wishlistInfo) {
              notifymeButton = wishlistInfo.querySelector(
                "[data-js-component='NotifyMeComponent']"
              );
              addToCartButton = wishlistInfo.querySelector(
                "[data-js-component='AddToCartComponent']"
              );
            }

            if (notifymeButton) {
              notifymeButton.querySelector('#productCode')
                ? (notifymeButton.querySelector('#productCode').value =
                  event.target.value)
                : '';
            }

            if (addToCartButton) {
              this.$emit(this.$customEvents.PRODUCT_EVENTS.sizechangedwish, {
                itemIndex: wishlistInfo.dataset.wishlistInfo,
                productCode: event.target.value,
              });
            }

            const isUnavailable =
              event.target.selectedOptions[0].dataset.unavailable == 'true';
            if (isUnavailable) {
              this.$emit(this.$customEvents.PRODUCT_EVENTS.notifyme, {
                productCode: selectedOption.dataset.selectedProductCode,
              });
            } else {
              this.$emit(this.$customEvents.PRODUCT_EVENTS.notifymeclose, {
                productCode: selectedOption.dataset.selectedProductCode,
              });
            }
          }
        });
      });
    }
  }

  // Open modal for low stock notifications
  openModal(url) {
    fetch(url)
      .then(response => response.text())
      .then(content => {
        vex.open({
          unsafeContent: content,
          className: '--functional',
          closeClassName: 'icon close',
          showCloseButton: true,
          afterOpen: function () {
            this.closeEl.classList.remove('vex-close');
          }
        });
      })
      .catch(error => console.error('Error loading modal:', error));
  }

  bindEvents() {
    let ctx = this;
    // Update PDP wishlist button
    const updatePDPWishlistButton = function (payload, removedAction = false) {
      // search the btn with the base product code
      const wishlistButton = document.querySelector(
        '[data-wishlist-code="' + payload.baseProductCode + '"]'
      );
      if (wishlistButton) {
        const isPdpWishlistButton = wishlistButton.hasAttribute(
          ctx.SELECTORS.pdpWishlistButtonAttribute
        );
        if (!removedAction) wishlistButton.dataset.code = payload.productCode;
        ctx.wishlistLogic
          .wishlistCheckAjax(payload.productCode)
          .then((result) => {
            if (result === 'true') {
              ctx.wishlistLogic.updatePDPButton(
                wishlistButton,
                true,
                isPdpWishlistButton
              );
            } else {
              ctx.wishlistLogic.updatePDPButton(
                wishlistButton,
                false,
                isPdpWishlistButton
              );
            }
          });
      }
    };

    //PDP size change
    this.$on(this.$customEvents.PRODUCT_EVENTS.sizechanged, ({payload}) => {
      updatePDPWishlistButton(payload);
    });

    // Remove from mini wishlist
    this.$on(this.$customEvents.MINIWISHLIST_EVENTS.removed, ({payload}) => {
      updatePDPWishlistButton(payload, true);
    });

    // event simplebar loader
    this.$on(
      this.$customEvents.SIMPLEBAR_EVENTS.loaded,
      ({event, payload}) => {
        const wishlistButtons = payload.elem.querySelectorAll(
          this.SELECTORS.wishBtn
        );
        this.handleWishlistClick(wishlistButtons);
      }
    );

    // Size not selected event wishlist page
    this.$on(
      this.$customEvents.PRODUCT_EVENTS.sizerequired,
      ({event, payload}) => {
        try {
          const sizeBox = document.querySelector(
            '[data-sizes-code=prod_' +
            payload?.getAttribute('data-product-code') +
            ']'
          );
          if (sizeBox?.querySelector('.custom-select')) {
            sizeBox
              .querySelector('.custom-select')
              .classList.add(this.CLASSES.error);
          }
        } catch (e) {
          console.error(e);
        }
      }
    );

    // Rebind wishlist buttons when recommendation engine updates
    this.$on(
      this.$customEvents.RECOMMENDATION_ENGINE_EVENTS.updated,
      ({event, payload}) => {
        const container = payload?.elem[0] || document;
        this.wishBtns = container.querySelectorAll(this.SELECTORS.wishBtn);
        this.handleWishlistClick(this.wishBtns);
      }
    );

    // Rebind wishlist buttons when Google recommendation engine updates
    this.$on(
      this.$customEvents.RECOMMENDATION_ENGINE_EVENTS.updatedGoogle,
      ({event, payload}) => {
        const container = payload?.elem[0] || document;
        this.wishBtns = container.querySelectorAll(this.SELECTORS.wishBtn);
        this.handleWishlistClick(this.wishBtns);
      }
    );

    // listing when fired pagination and infinite scroll
    this.$on(
      this.$customEvents.INFINITE_SCROLL_EVENTS.loaded,
      ({event, payload}) => {
        const newProducts = payload.newProducts;
        const wishlistBtns = [];
        if (newProducts) {
          newProducts.forEach((newProduct) => {
            const wishlistBtn = newProduct.querySelector(
              this.SELECTORS.wishBtn
            );
            wishlistBtn ? wishlistBtns.push(wishlistBtn) : '';
          });
        }
        this.handleWishlistClick(wishlistBtns);
      }
    );

    // Handle click event for PDP add to wishlist button
    if (this.pdpWishlistButton) {
      this.pdpWishlistButton.addEventListener('click', (event) =>
        this.handleSingleWishlistClick(event, this.pdpWishlistButton)
      );
    } else {
      console.warn(
        'Absence of PDP wishlist button; this can carry to functionality problems'
      );
    }
  }

  render() {
    this.log('Rendering...');
    this.bindEvents();
    this.handleWishlistClick(this.wishBtns);
    this.handleDeleteFromWishlist();
    this.listenSelectWishSize();
    this.wishlistCardItemEvents();
  }
}
